import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    IBeforeInstallPromptEvent,
    INavigatorWithStandalone,
} from '../../interfaces/pwa-install.interfaces';
import {
    TPwaInstallMode,
    TPwaInstallOutcome,
} from '../../interfaces/pwa-install.types';

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const STANDALONE_MEDIA_QUERY = '(display-mode: standalone)';

@Injectable({
    providedIn: 'root',
})
export class PwaInstallService {
    private readonly modeSubject = new BehaviorSubject<TPwaInstallMode>(
        'hidden',
    );
    private readonly mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    private readonly standaloneMediaQuery = window.matchMedia(
        STANDALONE_MEDIA_QUERY,
    );

    public readonly mode$: Observable<TPwaInstallMode> =
        this.modeSubject.asObservable();

    private deferredPrompt: IBeforeInstallPromptEvent | null = null;
    private initialized = false;
    private installed = false;

    public constructor(private readonly ngZone: NgZone) {}

    public initialize(): void {
        if (this.initialized) {
            this.updateMode();
            return;
        }

        this.initialized = true;
        this.installed = this.isStandalone();

        window.addEventListener(
            'beforeinstallprompt',
            this.onBeforeInstallPrompt,
        );
        window.addEventListener('appinstalled', this.onAppInstalled);
        this.mobileMediaQuery.addEventListener('change', this.onMediaChange);
        this.standaloneMediaQuery.addEventListener(
            'change',
            this.onMediaChange,
        );

        this.updateMode();
    }

    public async install(): Promise<TPwaInstallOutcome> {
        const mode = this.modeSubject.value;

        if (mode === 'ios-instructions' || mode === 'android-instructions') {
            return 'instructions-shown';
        }

        if (mode !== 'native' || !this.deferredPrompt) {
            return 'unavailable';
        }

        const promptEvent = this.deferredPrompt;
        this.deferredPrompt = null;
        this.updateMode();

        try {
            await promptEvent.prompt();
            const choice = await promptEvent.userChoice;

            return choice.outcome === 'accepted' ? 'accepted' : 'dismissed';
        } catch (error) {
            console.error('Не удалось открыть диалог установки PWA', error);
            return 'unavailable';
        }
    }

    private readonly onBeforeInstallPrompt = (event: Event): void => {
        event.preventDefault();

        this.ngZone.run(() => {
            this.deferredPrompt = event as IBeforeInstallPromptEvent;
            this.updateMode();
        });
    };

    private readonly onAppInstalled = (): void => {
        this.ngZone.run(() => {
            this.installed = true;
            this.deferredPrompt = null;
            this.updateMode();
        });
    };

    private readonly onMediaChange = (): void => {
        this.ngZone.run(() => this.updateMode());
    };

    private updateMode(): void {
        let nextMode: TPwaInstallMode = 'hidden';

        if (
            this.mobileMediaQuery.matches &&
            !this.installed &&
            !this.isStandalone()
        ) {
            if (this.deferredPrompt) {
                nextMode = 'native';
            } else if (this.isIosDevice()) {
                nextMode = 'ios-instructions';
            } else if (this.isAndroidChromiumBrowser()) {
                nextMode = 'android-instructions';
            }
        }

        if (this.modeSubject.value !== nextMode) {
            this.modeSubject.next(nextMode);
        }
    }

    private isStandalone(): boolean {
        const standaloneNavigator = navigator as INavigatorWithStandalone;

        return (
            this.standaloneMediaQuery.matches ||
            standaloneNavigator.standalone === true
        );
    }

    private isIosDevice(): boolean {
        const isIosUserAgent = /iPad|iPhone|iPod/i.test(navigator.userAgent);
        const isTouchEnabledIpad =
            navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

        return isIosUserAgent || isTouchEnabledIpad;
    }

    private isAndroidChromiumBrowser(): boolean {
        const userAgent = navigator.userAgent;
        const isAndroid = /Android/i.test(userAgent);
        const isChromiumBrowser =
            /(?:Chrome|Chromium|EdgA|OPR|SamsungBrowser)\//i.test(userAgent);
        const isWebView =
            /\bwv\b/i.test(userAgent) ||
            /Version\/4\.0.*Chrome\//i.test(userAgent);

        return isAndroid && isChromiumBrowser && !isWebView;
    }
}
